// ไฟล์: frontend/src/pages/ResourceDetailPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './ResourceDetailPage.css';

const ResourceDetailPage = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [inLibrary, setInLibrary] = useState(false);
  const [similarResources, setSimilarResources] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);

  // ดึงข้อมูลสื่อการสอนและความคิดเห็น
  useEffect(() => {
    const fetchResource = async () => {
      try {
        const response = await axios.get(`/resources/${id}`);
        setResource(response.data.resource);
        setComments(response.data.comments);
        
        // ดึงข้อมูลสื่อการสอนที่คล้ายกัน (ในวิชาเดียวกัน)
        if (response.data.resource) {
          const subjectId = response.data.resource.subject_id;
          const similarRes = await axios.get(`/resources?subject=${subjectId}`);
          
          // กรองสื่อที่ไม่ใช่สื่อปัจจุบัน และจำกัดจำนวน 5 รายการ
          const filtered = similarRes.data.resources
            .filter(item => item.id !== parseInt(id))
            .slice(0, 5);
          
          setSimilarResources(filtered);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching resource:', error);
        setLoading(false);
      }
    };

    fetchResource();
  }, [id]);

  // ตรวจสอบว่าผู้ใช้ให้คะแนนไว้หรือไม่ และอยู่ในคลังส่วนตัวหรือไม่
  useEffect(() => {
    const checkUserRating = async () => {
      if (user && resource) {
        try {
          // ตรวจสอบคะแนนของผู้ใช้
          const ratingRes = await axios.get(`/ratings/user?resource_id=${id}`);
          if (ratingRes.data.rating) {
            setUserRating(ratingRes.data.rating);
          }
          
          // ตรวจสอบว่าอยู่ในคลังส่วนตัวหรือไม่
          const libraryRes = await axios.get('/library');
          const inLib = libraryRes.data.items.some(item => 
            item.resource_id === parseInt(id)
          );
          setInLibrary(inLib);
        } catch (error) {
          console.error('Error checking user data:', error);
        }
      }
    };

    checkUserRating();
  }, [id, user, resource]);

  // จัดการการเปลี่ยนแปลงความคิดเห็น
  const handleCommentChange = (e) => {
    setCommentText(e.target.value);
  };

  // จัดการการส่งความคิดเห็น
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      Swal.fire({
        icon: 'info',
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนจึงจะสามารถแสดงความคิดเห็นได้',
        showCancelButton: true,
        confirmButtonText: 'เข้าสู่ระบบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }
    
    if (!commentText.trim()) {
      return;
    }
    
    setCommentLoading(true);
    
    try {
      const response = await axios.post('/comments', {
        content: commentText,
        resource_id: id
      });
      
      // เพิ่มความคิดเห็นใหม่ลงในรายการ
      setComments([response.data.comment, ...comments]);
      setCommentText('');
    } catch (error) {
      console.error('Error adding comment:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถแสดงความคิดเห็นได้',
        text: 'เกิดข้อผิดพลาดในการแสดงความคิดเห็น กรุณาลองใหม่อีกครั้ง'
      });
    } finally {
      setCommentLoading(false);
    }
  };

  // จัดการการลบความคิดเห็น
  const handleDeleteComment = async (commentId) => {
    Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการลบความคิดเห็น',
      text: 'คุณแน่ใจหรือไม่ว่าต้องการลบความคิดเห็นนี้?',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d33'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`/comments/${commentId}`);
          
          // อัพเดทรายการความคิดเห็น
          setComments(comments.filter(comment => comment.id !== commentId));
          
          Swal.fire({
            icon: 'success',
            title: 'ลบความคิดเห็นสำเร็จ',
            timer: 1500,
            showConfirmButton: false
          });
        } catch (error) {
          console.error('Error deleting comment:', error);
          
          Swal.fire({
            icon: 'error',
            title: 'ไม่สามารถลบความคิดเห็นได้',
            text: 'เกิดข้อผิดพลาดในการลบความคิดเห็น กรุณาลองใหม่อีกครั้ง'
          });
        }
      }
    });
  };

  // จัดการการให้คะแนน
  const handleRating = async (rating) => {
    if (!user) {
      Swal.fire({
        icon: 'info',
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนจึงจะสามารถให้คะแนนได้',
        showCancelButton: true,
        confirmButtonText: 'เข้าสู่ระบบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }
    
    try {
      await axios.post('/ratings', {
        rating,
        resource_id: id
      });
      
      // อัพเดทคะแนนในหน้าจอ
      setUserRating(rating);
      
      // อัพเดทคะแนนเฉลี่ยของสื่อการสอน
      const newAvgRating = resource.rating_count === 0
        ? rating
        : ((resource.avg_rating * resource.rating_count) + rating) / (resource.rating_count + 1);
      
      setResource({
        ...resource,
        avg_rating: newAvgRating,
        rating_count: resource.rating_count + (userRating === 0 ? 1 : 0)
      });
      
      Swal.fire({
        icon: 'success',
        title: 'ให้คะแนนสำเร็จ',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error rating resource:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถให้คะแนนได้',
        text: 'เกิดข้อผิดพลาดในการให้คะแนน กรุณาลองใหม่อีกครั้ง'
      });
    }
  };

  // จัดการการดาวน์โหลด
  const handleDownload = async () => {
    try {
      window.open(`${process.env.REACT_APP_API_URL}/resources/${id}/download`, '_blank');
    } catch (error) {
      console.error('Error downloading resource:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถดาวน์โหลดได้',
        text: 'เกิดข้อผิดพลาดในการดาวน์โหลดไฟล์ กรุณาลองใหม่อีกครั้ง'
      });
    }
  };

  // จัดการการเพิ่ม/ลบจากคลังส่วนตัว
  const handleLibrary = async () => {
    if (!user) {
      Swal.fire({
        icon: 'info',
        title: 'กรุณาเข้าสู่ระบบ',
        text: 'คุณต้องเข้าสู่ระบบก่อนจึงจะสามารถบันทึกลงคลังส่วนตัวได้',
        showCancelButton: true,
        confirmButtonText: 'เข้าสู่ระบบ',
        cancelButtonText: 'ยกเลิก'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login');
        }
      });
      return;
    }
    
    try {
      if (inLibrary) {
        // ค้นหา ID ในคลังส่วนตัว
        const libraryRes = await axios.get('/library');
        const libraryItem = libraryRes.data.items.find(item => item.resource_id === parseInt(id));
        
        if (libraryItem) {
          await axios.delete(`/library/${libraryItem.library_id}`);
          
          setInLibrary(false);
          
          Swal.fire({
            icon: 'success',
            title: 'ลบออกจากคลังส่วนตัวสำเร็จ',
            timer: 1500,
            showConfirmButton: false
          });
        }
      } else {
        await axios.post('/library', {
          resource_id: id
        });
        
        setInLibrary(true);
        
        Swal.fire({
          icon: 'success',
          title: 'บันทึกลงคลังส่วนตัวสำเร็จ',
          timer: 1500,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error managing library:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'ไม่สามารถจัดการคลังส่วนตัวได้',
        text: 'เกิดข้อผิดพลาดในการจัดการคลังส่วนตัว กรุณาลองใหม่อีกครั้ง'
      });
    }
  };

  // จัดการการแชร์
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: resource.title,
        text: resource.description,
        url: window.location.href
      })
      .catch(error => console.error('Error sharing:', error));
    } else {
      // คัดลอกลิงก์
      navigator.clipboard.writeText(window.location.href)
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'คัดลอกลิงก์สำเร็จ',
            text: 'ลิงก์ถูกคัดลอกไปยังคลิปบอร์ดแล้ว',
            timer: 1500,
            showConfirmButton: false
          });
        })
        .catch(error => console.error('Error copying link:', error));
    }
  };

  // ฟอร์แมตวันที่
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="loading-container">
        {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
      </div>
    );
  }

  // หากไม่พบสื่อการสอน
  if (!resource) {
    return (
      <div className="container">
        <div className="resource-not-found">
          <div className="not-found-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h2>ไม่พบสื่อการสอนที่ต้องการ</h2>
          <p>สื่อการสอนที่คุณกำลังค้นหาอาจถูกลบไปแล้วหรือไม่มีอยู่ในระบบ</p>
          <Link to="/resources" className="btn btn-primary">
            <i className="fas fa-arrow-left"></i> กลับไปยังหน้าสื่อการสอน
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="resource-detail-page">
      <div className="container">
        <div className="resource-breadcrumb">
          <Link to="/resources">
            <i className="fas fa-home"></i> สื่อการสอน
          </Link>
          <span className="breadcrumb-separator">
            <i className="fas fa-chevron-right"></i>
          </span>
          <span>{resource.title}</span>
        </div>
        
        <div className="resource-detail-header">
          <div className="resource-detail-info">
            <h1 className="resource-detail-title">{resource.title}</h1>
            
            <div className="resource-detail-meta">
              <div className="meta-item">
                <i className="fas fa-user"></i> โดย: {resource.author_name}
              </div>
              <div className="meta-item">
                <i className="fas fa-folder"></i> หมวดหมู่: {resource.category_name}
              </div>
              <div className="meta-item">
                <i className="fas fa-book"></i> วิชา: {resource.subject_name}
              </div>
              <div className="meta-item">
                <i className="fas fa-graduation-cap"></i> ระดับชั้น: {resource.grade_level_name}
              </div>
              <div className="meta-item">
                <i className="fas fa-calendar-alt"></i> วันที่: {formatDate(resource.created_at)}
              </div>
            </div>
            
            <div className="resource-detail-stats">
              <div className="stat-item">
                <i className="fas fa-eye"></i> {resource.view_count} ครั้ง
              </div>
              <div className="stat-item">
                <i className="fas fa-download"></i> {resource.download_count} ครั้ง
              </div>
              <div className="stat-item">
                <div className="rating">
                  {[1, 2, 3, 4, 5].map(star => (
                    <i 
                      key={star}
                      className={`fas fa-star ${star <= Math.round(resource.avg_rating || 0) ? 'filled' : ''}`}
                    ></i>
                  ))}
                                <span className="rating-value">
                  {resource.avg_rating !== undefined && resource.avg_rating !== null && !isNaN(Number(resource.avg_rating))
                    ? Number(resource.avg_rating).toFixed(1)
                    : '0.0'}
                </span>
                  <span className="rating-count">
                    ({resource.rating_count} คะแนน)
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="resource-detail-thumbnail">
            <img 
              src={resource.thumbnail 
                ? `${process.env.REACT_APP_API_URL}/uploads/${resource.thumbnail}`
                : '/assets/default-resource.jpg'} 
              alt={resource.title} 
            />
            <div className="resource-type-badge">
              {resource.file_type === 'pdf' && <i className="fas fa-file-pdf"></i>}
              {resource.file_type === 'ppt' && <i className="fas fa-file-powerpoint"></i>}
              {resource.file_type === 'video' && <i className="fas fa-video"></i>}
              {resource.file_type === 'link' && <i className="fas fa-link"></i>}
              {resource.file_type === 'drive' && <i className="fab fa-google-drive"></i>}
              {resource.file_type === 'other' && <i className="fas fa-file"></i>}
              {resource.file_type}
            </div>
          </div>
        </div>
        
        <div className="resource-detail-actions">
          {/* ปุ่มดาวน์โหลด */}
          {resource.file_path && (
            <button className="btn btn-primary" onClick={handleDownload}>
              <i className="fas fa-download"></i> ดาวน์โหลด
            </button>
          )}
          
          {/* ปุ่มเปิดลิงก์ */}
          {resource.external_link && (
            <a 
              href={resource.external_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              <i className="fas fa-external-link-alt"></i> เปิดลิงก์
            </a>
          )}
          
          {/* ปุ่มบันทึกลงคลัง */}
          <button 
            className={`btn ${inLibrary ? 'btn-danger' : 'btn-light'}`}
            onClick={handleLibrary}
          >
            <i className={`fas ${inLibrary ? 'fa-trash' : 'fa-bookmark'}`}></i>
            {inLibrary ? ' นำออกจากคลัง' : ' บันทึกลงคลัง'}
          </button>
          
          {/* ปุ่มแชร์ */}
          <button className="btn btn-light" onClick={handleShare}>
            <i className="fas fa-share-alt"></i> แชร์
          </button>
        </div>
        
        <div className="resource-detail-content">
          <div className="resource-detail-main">
            <div className="detail-card">
              <div className="detail-card-header">
                <h2>
                  <i className="fas fa-info-circle"></i> รายละเอียด
                </h2>
              </div>
              <div className="detail-card-body">
                <p className="resource-description">{resource.description}</p>
              </div>
            </div>
            
            <div className="detail-card">
              <div className="detail-card-header">
                <h2>
                  <i className="fas fa-star"></i> ให้คะแนน
                </h2>
              </div>
              <div className="detail-card-body">
                <div className="rating-form">
                  <p>คลิกที่ดาวเพื่อให้คะแนนสื่อการสอนนี้</p>
                  <div 
                    className="rating-stars"
                    onMouseLeave={() => setHoverRating(0)}
                  >
                    {[1, 2, 3, 4, 5].map(star => (
                      <i 
                        key={star}
                        className={`fas fa-star ${star <= (hoverRating || userRating) ? 'filled' : ''}`}
                        onClick={() => handleRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                      ></i>
                    ))}
                  </div>
                  <p className="rating-message">
                    {userRating > 0 
                      ? `คุณให้คะแนน ${userRating} ดาว` 
                      : 'คุณยังไม่ได้ให้คะแนน'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="detail-card">
              <div className="detail-card-header">
                <h2>
                  <i className="fas fa-comments"></i> ความคิดเห็น ({comments.length})
                </h2>
              </div>
              <div className="detail-card-body">
                <form className="comment-form" onSubmit={handleSubmitComment}>
                  <textarea
                    className="form-control"
                    placeholder="แสดงความคิดเห็นของคุณ..."
                    value={commentText}
                    onChange={handleCommentChange}
                    disabled={commentLoading}
                  ></textarea>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={!commentText.trim() || commentLoading}
                  >
                    {commentLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i> กำลังส่ง...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane"></i> ส่งความคิดเห็น
                      </>
                    )}
                  </button>
                </form>
                
                <div className="comments-list">
                  {comments.length === 0 ? (
                    <div className="no-comments">
                      <i className="fas fa-comments"></i>
                      <p>ยังไม่มีความคิดเห็น เป็นคนแรกที่แสดงความคิดเห็น</p>
                    </div>
                  ) : (
                    comments.map(comment => (
                      <div className="comment" key={comment.id}>
                        <div className="comment-avatar">
                          <img 
                            src={comment.profile_image 
                              ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${comment.profile_image}`
                              : `https://ui-avatars.com/api/?name=${comment.fullname}&background=random`} 
                            alt={comment.fullname} 
                          />
                        </div>
                        <div className="comment-content">
                          <div className="comment-header">
                            <div className="comment-author">{comment.fullname}</div>
                            <div className="comment-date">{formatDate(comment.created_at)}</div>
                          </div>
                          <div className="comment-text">{comment.content}</div>
                          {user && (user.id === comment.user_id || user.role === 'admin') && (
                            <div className="comment-actions">
                              <button 
                                className="comment-action"
                                onClick={() => handleDeleteComment(comment.id)}
                              >
                                <i className="fas fa-trash"></i> ลบ
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="resource-detail-sidebar">
            <div className="detail-card">
              <div className="detail-card-header">
                <h2>
                  <i className="fas fa-user"></i> เกี่ยวกับผู้แบ่งปัน
                </h2>
              </div>
              <div className="detail-card-body">
                <div className="author-info">
                  <div className="author-avatar">
                    <img 
                      src={resource.author_image 
                        ? `${process.env.REACT_APP_API_URL}/uploads/profiles/${resource.author_image}`
                        : `https://ui-avatars.com/api/?name=${resource.author_name}&background=random&size=200`} 
                      alt={resource.author_name} 
                    />
                  </div>
                  <div className="author-details">
                    <h3 className="author-name">{resource.author_name}</h3>
                    <Link to={`/teachers/${resource.user_id}`} className="btn btn-sm btn-outline">
                      <i className="fas fa-user"></i> ดูโปรไฟล์
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            
            {similarResources.length > 0 && (
              <div className="detail-card">
                <div className="detail-card-header">
                  <h2>
                    <i className="fas fa-link"></i> สื่อการสอนที่เกี่ยวข้อง
                  </h2>
                </div>
                <div className="detail-card-body">
                  <div className="similar-resources">
                    {similarResources.map(similar => (
                      <div className="similar-resource" key={similar.id}>
                        <img 
                          src={similar.thumbnail 
                            ? `${process.env.REACT_APP_API_URL}/uploads/${similar.thumbnail}`
                            : '/assets/default-resource.jpg'} 
                          alt={similar.title} 
                          className="similar-resource-image"
                        />
                        <div className="similar-resource-info">
                          <h3 className="similar-resource-title">
                            <Link to={`/resources/${similar.id}`}>{similar.title}</Link>
                          </h3>
                          <div className="similar-resource-meta">
                            <span>
                              <i className="fas fa-user"></i> {similar.author_name}
                            </span>
                            <span>
                              <i className="fas fa-eye"></i> {similar.view_count}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceDetailPage;
